import {
  formatCompetitorAdsItemsForGallery,
  normalizeCompetitorName,
} from "../../services/competitorAdsGallery";
import {
  AI_COMPETITORS_ADS_CENSOR_RATIO,
  AI_COMPETITORS_ADS_UNLOCKED_MAX,
} from "./aiCompetitorsConstants";
import { getIntlLocaleCode } from "../../utils/i18nLanguage";

const getAdIdentity = (ad, index) =>
  String(ad?.id || ad?.libraryID || `${ad?.url || "ad"}-${index}`);

const getAdPlatform = (ad) =>
  String(ad?.platform || "unknown")
    .trim()
    .toLowerCase();

const getAdCompetitorKey = (ad, index) =>
  normalizeCompetitorName(ad?.competitor) || `unknown-${index}`;

const takeNextFromRotatingPlatforms = (platformQueues, cursor) => {
  if (!platformQueues.length) return { ad: null, cursor };
  for (let offset = 0; offset < platformQueues.length; offset += 1) {
    const index = (cursor + offset) % platformQueues.length;
    const queue = platformQueues[index];
    if (queue.length > 0) {
      return {
        ad: queue.shift(),
        cursor: (index + 1) % platformQueues.length,
      };
    }
  }
  return { ad: null, cursor };
};

export const pickBalancedPreviewAds = (ads = [], { max } = {}) => {
  if (!Array.isArray(ads) || ads.length === 0) return [];

  const requestedMax = Number(max);
  const limit = Math.min(
    Number.isFinite(requestedMax) && requestedMax > 0
      ? requestedMax
      : ads.length,
    ads.length
  );
  const competitorQueues = new Map();

  ads.forEach((ad, index) => {
    const competitorKey = getAdCompetitorKey(ad, index);
    const platform = getAdPlatform(ad);
    if (!competitorQueues.has(competitorKey)) {
      competitorQueues.set(competitorKey, new Map());
    }
    const platforms = competitorQueues.get(competitorKey);
    if (!platforms.has(platform)) {
      platforms.set(platform, []);
    }
    platforms.get(platform).push(ad);
  });

  const competitors = [...competitorQueues.values()].map((platforms) => ({
    platformQueues: [...platforms.values()],
    cursor: 0,
  }));

  const picked = [];
  const used = new Set();
  let round = 0;

  while (picked.length < limit) {
    const takePerCompetitor = round % 2 === 0 ? 1 : 2;
    let addedInRound = 0;

    for (const competitor of competitors) {
      for (let taken = 0; taken < takePerCompetitor; taken += 1) {
        if (picked.length >= limit) break;
        const next = takeNextFromRotatingPlatforms(
          competitor.platformQueues,
          competitor.cursor
        );
        competitor.cursor = next.cursor;
        if (!next.ad) break;
        const identity = getAdIdentity(next.ad, picked.length);
        if (used.has(identity)) continue;
        used.add(identity);
        picked.push(next.ad);
        addedInRound += 1;
      }
      if (picked.length >= limit) break;
    }

    if (addedInRound === 0) break;
    round += 1;
  }

  return picked;
};

export const groupAdsByCompetitor = ({ competitors = [], ads = [] }) => {
  const groups = competitors.map((competitor) => ({
    competitor,
    ads: [],
  }));
  const unmatched = [];

  ads.forEach((ad) => {
    const adName = normalizeCompetitorName(ad?.competitor);
    const match = groups.find((group) => {
      const groupName = normalizeCompetitorName(group.competitor.name);
      if (!adName || !groupName) return false;
      return (
        adName === groupName ||
        adName.includes(groupName) ||
        groupName.includes(adName)
      );
    });
    if (match) {
      match.ads.push(ad);
      return;
    }
    unmatched.push(ad);
  });

  unmatched.forEach((ad) => {
    const label = String(ad?.competitor || "").trim();
    if (!label) {
      if (groups[0]) groups[0].ads.push(ad);
      return;
    }
    const existing = groups.find(
      (group) =>
        normalizeCompetitorName(group.competitor.name) ===
        normalizeCompetitorName(label)
    );
    if (existing) {
      existing.ads.push(ad);
      return;
    }
    groups.push({
      competitor: {
        id: `unmatched-${normalizeCompetitorName(label)}`,
        name: label,
        domain: "",
        source: "suggested",
      },
      ads: [ad],
    });
  });

  const withAds = groups.filter((group) => group.ads.length > 0);
  const withoutAds = groups.filter((group) => group.ads.length === 0);
  return [...withAds, ...withoutAds];
};

export const getLockedAdsCount = (total = 0) => {
  const count = Number(total) || 0;
  if (count <= 1) return 0;
  const ratioLocked = Math.max(
    1,
    Math.ceil(count * AI_COMPETITORS_ADS_CENSOR_RATIO)
  );
  const unlocked = Math.min(
    AI_COMPETITORS_ADS_UNLOCKED_MAX,
    count - ratioLocked
  );
  return count - unlocked;
};

export const markLockedAds = (ads = []) => {
  const lockedCount = getLockedAdsCount(ads.length);
  const unlockedCount = ads.length - lockedCount;
  return ads.map((ad, index) => ({
    ...ad,
    isLocked: index >= unlockedCount,
  }));
};

export const formatCompetitorNamesList = (names = [], language = "en") => {
  const cleaned = names
    .map((name) => String(name || "").trim())
    .filter(Boolean);
  if (cleaned.length === 0) return "";
  try {
    return new Intl.ListFormat(getIntlLocaleCode(language), {
      style: "long",
      type: "conjunction",
    }).format(cleaned);
  } catch (error) {
    return cleaned.join(", ");
  }
};

export const getAdFormatKey = (item) => {
  const hasCarousel =
    Array.isArray(item?.additionalMedia) && item.additionalMedia.length > 0;
  if (hasCarousel) return "carousel";
  if (item?.type === "video") return "video";
  return "image";
};

const toList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return Object.values(value);
  return [];
};

const normalizeAdRecord = (ad) => {
  if (!ad || typeof ad !== "object") return ad;
  return {
    ...ad,
    additionalMedia: toList(ad.additionalMedia),
  };
};

const normalizePageValue = (pageValue) => {
  if (!pageValue) return pageValue;
  if (Array.isArray(pageValue)) return pageValue.map(normalizeAdRecord);
  if (typeof pageValue !== "object") return pageValue;

  if (
    pageValue.ads &&
    typeof pageValue.ads === "object" &&
    !Array.isArray(pageValue.ads)
  ) {
    const ads = {};
    Object.entries(pageValue.ads).forEach(([id, ad]) => {
      ads[id] = normalizeAdRecord(ad);
    });
    return { ...pageValue, ads };
  }

  const next = {};
  Object.entries(pageValue).forEach(([id, ad]) => {
    next[id] = normalizeAdRecord(ad);
  });
  return next;
};

export const adsFromAiCompetitorsSession = (session) => {
  const adsPages = session?.adsPages;
  if (!adsPages || typeof adsPages !== "object") return [];

  const normalized = {};
  Object.entries(adsPages).forEach(([platform, pageMap]) => {
    if (!pageMap || typeof pageMap !== "object") return;
    normalized[platform] = {};
    Object.entries(pageMap).forEach(([pageId, pageValue]) => {
      normalized[platform][pageId] = normalizePageValue(pageValue);
    });
  });

  return formatCompetitorAdsItemsForGallery(normalized);
};
